import axios from 'axios';

import type { AxisOption } from 'components/molecules/PackArchitecture/AxisConfigDropdown';
import type { KpiItem, PricePackBubbleRow, PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';
import { getEffectiveChartRowCount } from 'components/pages/PackArchitecture/packArchitectureChartRequest.helpers';
import { ppaUrls } from 'config/urls';
import { formatNumWithKMB } from 'utils/helperMethods';

export type PackArchitectureExportScope = 'current_view' | 'all_data';
export type PackArchitectureExportFormat = 'xlsx' | 'csv';

export const MAX_EXPORT_ROWS = 10000;

/**
 * Export columns are:
 *   Segment | X-Axis | Y-Axis | Y-Axis Growth % (vs YA) | …Size KPI pairs…
 *
 * Each "Size" KPI (bubble_size axis column option, e.g. Value/Volume/Unit
 * Sales) drives one `<label>` + `<label> Growth % (vs YA)` column pair —
 * automatically, so adding a new Size KPI in config requires no export
 * code changes.
 */
export type PackArchitectureSizeKpiOption = AxisOption;

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

type SizeKpiCellValue = {
    raw: number | null;
    formatted: string;
    prefix: string;
    growth: number | null;
};

type ExportCellData = {
    yAxisRaw: number | null;
    yAxisFormatted: string;
    yAxisPrefix: string;
    yAxisGrowth: number | null;
    /** Index-aligned with the `sizeKpiOptions` list passed into the builder. */
    sizeKpiValues: SizeKpiCellValue[];
};

type ExportGrid = {
    xAxisEntities: Array<{ key: string; label: string }>;
    bubbleRepMap: Map<string, { key: string; label: string }>;
    grid: Map<string, Map<string, ExportCellData>>;
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const findKpiInList = (
    kpis: KpiItem[] | undefined,
    targetId: string,
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean,
): KpiItem | undefined => (kpis ?? []).find((k) => matchesKpiId(k.kpi_id, targetId));

/** Formats a KPI value for display in CSV (1 decimal, with prefix). Mirrors table display. */
const formatKpiForCsv = (kpi: KpiItem | undefined, kpiId: string, yAxisKpiId: string): string => {
    if (!kpi) return '-';
    const prefix = kpi.valuePrefix ?? '';
    const raw = kpi.raw_value;
    if (typeof raw !== 'number' || !Number.isFinite(raw)) {
        const val = (kpi.value ?? '').trim();
        return val && val !== 'null' ? val : '-';
    }
    const isPrice = kpiId === yAxisKpiId || /price/i.test(kpiId);
    if (isPrice) return `${prefix}${raw.toFixed(1)}`;
    const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
    const formatted = `${prefix}${compact.format(raw)}`;
    return formatted.replace(/(\d(?:\.\d+)?)M+$/i, '$1');
};

const formatGrowthForCsv = (growthPct: number | undefined | null): string => {
    if (growthPct == null || !Number.isFinite(growthPct)) return '-';
    const sign = growthPct < 0 ? '-' : '';
    return `${sign}${formatNumWithKMB(Math.abs(growthPct), 1)}%`;
};

const buildExcelFormat = (prefix: string): string =>
    prefix ? `"${prefix}"#,##0.0` : '#,##0.0';

// ---------------------------------------------------------------------------
// Entity key/label helpers (extracted to keep grid builder complexity ≤ 15)
// ---------------------------------------------------------------------------

type EntityLike = { ex_id?: string; description?: string };

const entityKey = (e: EntityLike): string => e.ex_id || e.description || '';
const entityLabel = (e: EntityLike): string => e.description || e.ex_id || '';

// ---------------------------------------------------------------------------
// Grid builder — iterates raw API rows into a structured grid
// ---------------------------------------------------------------------------

const buildSizeKpiCellValue = (
    segItem: PricePackBubbleRow['data'][0]['data'][0],
    sizeKpiOption: PackArchitectureSizeKpiOption,
    yAxis: string,
    matchesKpiId: (a: string, b: string) => boolean,
): SizeKpiCellValue => {
    const kpi = findKpiInList(segItem.kpis, sizeKpiOption.value, matchesKpiId);
    return {
        raw: kpi?.raw_value ?? null,
        formatted: formatKpiForCsv(kpi, sizeKpiOption.value, yAxis),
        prefix: kpi?.valuePrefix ?? '',
        growth: kpi?.growth_percentage ?? null,
    };
};

const buildExportCellData = (
    segItem: PricePackBubbleRow['data'][0]['data'][0],
    yAxis: string,
    sizeKpiOptions: PackArchitectureSizeKpiOption[],
    matchesKpiId: (a: string, b: string) => boolean,
): ExportCellData => {
    const yAxisKpi = findKpiInList(segItem.kpis, yAxis, matchesKpiId);

    return {
        yAxisRaw: yAxisKpi?.raw_value ?? null,
        yAxisFormatted: formatKpiForCsv(yAxisKpi, yAxis, yAxis),
        yAxisPrefix: yAxisKpi?.valuePrefix ?? '',
        yAxisGrowth: yAxisKpi?.growth_percentage ?? null,
        sizeKpiValues: sizeKpiOptions.map((option) => buildSizeKpiCellValue(segItem, option, yAxis, matchesKpiId)),
    };
};

const buildExportGrid = (
    rows: PricePackBubbleRow[],
    yAxis: string,
    sizeKpiOptions: PackArchitectureSizeKpiOption[],
    matchesKpiId: (a: string, b: string) => boolean,
): ExportGrid => {
    const xAxisEntities: Array<{ key: string; label: string }> = [];
    const bubbleRepMap = new Map<string, { key: string; label: string }>();
    const grid = new Map<string, Map<string, ExportCellData>>();

    for (const group of rows) {
        const xKey = entityKey(group);
        if (!xKey) continue;

        if (!grid.has(xKey)) {
            xAxisEntities.push({ key: xKey, label: entityLabel(group) });
            grid.set(xKey, new Map());
        }
        const colCells = grid.get(xKey)!;

        for (const axisGroup of group.data ?? []) {
            for (const segItem of axisGroup.data ?? []) {
                const bubbleKey = entityKey(segItem);
                if (!bubbleKey) continue;
                if (!bubbleRepMap.has(bubbleKey)) {
                    bubbleRepMap.set(bubbleKey, { key: bubbleKey, label: entityLabel(segItem) });
                }
                if (colCells.has(bubbleKey)) continue;
                colCells.set(bubbleKey, buildExportCellData(segItem, yAxis, sizeKpiOptions, matchesKpiId));
            }
        }
    }

    return { xAxisEntities, bubbleRepMap, grid };
};

// ---------------------------------------------------------------------------
// Grid flattener — produces normalised [bubble, xAxis, ...kpis] rows
// ---------------------------------------------------------------------------

const toCellValue = (raw: number | null, formatted: string): string | number =>
    raw != null && Number.isFinite(raw) ? raw : formatted;

const formatExcelDisplayValue = (value: number, format: string): string => {
    if (format.includes('"%"')) {
        return `${value.toFixed(1)}%`;
    }

    const prefixMatch = format.match(/^"([^"]*)"/);
    const prefix = prefixMatch?.[1] ?? '';
    const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(value);

    return `${prefix}${formatted}`;
};

export const formatPackArchitectureExportRowsForCsv = (
    rows: Array<Array<string | number>>,
    columnFormats: Array<string | null>,
    headerRowIndex: number,
): Array<Array<string | number>> =>
    rows.map((row, rowIndex) => {
        if (rowIndex <= headerRowIndex) return row;

        return row.map((cell, colIndex) => {
            const format = columnFormats[colIndex];
            if (cell === '' || cell === '-' || format == null || typeof cell !== 'number') {
                return cell;
            }

            return formatExcelDisplayValue(cell, format);
        });
    });

const flattenExportGrid = ({
    xAxisEntities,
    bubbleRepMap,
    grid,
}: ExportGrid): Array<Array<string | number>> => {
    const dataRows: Array<Array<string | number>> = [];

    // Segment-major order (matches table rows × X-axis columns / PRD sample).
    for (const { key: bKey, label: bLabel } of bubbleRepMap.values()) {
        for (const { key: xKey, label: xLabel } of xAxisEntities) {
            const cell = grid.get(xKey)?.get(bKey);
            if (!cell) continue;
            dataRows.push([
                bLabel,
                xLabel,
                toCellValue(cell.yAxisRaw, cell.yAxisFormatted),
                toCellValue(cell.yAxisGrowth, formatGrowthForCsv(cell.yAxisGrowth)),
                ...cell.sizeKpiValues.flatMap(({ raw, formatted, growth }) => [
                    toCellValue(raw, formatted),
                    toCellValue(growth, formatGrowthForCsv(growth)),
                ]),
            ]);
        }
    }

    return dataRows;
};

// ---------------------------------------------------------------------------
// Public export builder
// ---------------------------------------------------------------------------

export type PackArchitectureNormalizedExportResult = {
    rows: Array<Array<string | number>>;
    columnFormats: Array<string | null>;
    headerRowIndex: number;
    dataRowCount: number;
};

type BuildNormalizedExportParams = {
    rows: PricePackBubbleRow[];
    yAxis: string;
    yAxisLabel: string;
    xAxisLabel: string;
    bubbleRepresentsLabel: string;
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean;
    /** Every "Size" KPI (bubble_size column option) — one column pair each, in order. */
    sizeKpiOptions: PackArchitectureSizeKpiOption[];
    metadataRows: Array<Array<string | number>>;
};

/** Detects each Size KPI's currency prefix from the first available cell, index-aligned with `sizeKpiOptions`. */
const detectSizeKpiPrefixes = (grid: ExportGrid['grid'], sizeKpiCount: number): string[] => {
    const prefixes = new Array<string>(sizeKpiCount).fill('');
    let remaining = sizeKpiCount;
    outer: for (const colCells of grid.values()) {
        for (const cell of colCells.values()) {
            cell.sizeKpiValues.forEach((value, index) => {
                if (!prefixes[index] && value.prefix) {
                    prefixes[index] = value.prefix;
                    remaining -= 1;
                }
            });
            if (remaining <= 0) break outer;
        }
    }
    return prefixes;
};

export const buildPackArchitectureNormalizedExportRows = ({
    rows,
    yAxis,
    yAxisLabel,
    xAxisLabel,
    bubbleRepresentsLabel,
    matchesKpiId,
    sizeKpiOptions,
    metadataRows,
}: BuildNormalizedExportParams): PackArchitectureNormalizedExportResult => {
    const exportGrid = buildExportGrid(rows, yAxis, sizeKpiOptions, matchesKpiId);
    const dataRows = flattenExportGrid(exportGrid);

    // Detect currency prefixes from first available cell
    let yAxisPrefix = '';
    for (const colCells of exportGrid.grid.values()) {
        for (const cell of colCells.values()) {
            if (cell.yAxisPrefix) {
                yAxisPrefix = cell.yAxisPrefix;
                break;
            }
        }
        if (yAxisPrefix) break;
    }
    const sizeKpiPrefixes = detectSizeKpiPrefixes(exportGrid.grid, sizeKpiOptions.length);

    const headerRow: Array<string | number> = [
        bubbleRepresentsLabel,
        xAxisLabel,
        yAxisLabel,
        `${yAxisLabel} Growth % (vs YA)`,
        ...sizeKpiOptions.flatMap((option) => [option.label, `${option.label} Growth % (vs YA)`]),
    ];

    // metadataRows + 2 blank rows + header + data
    const allRows: Array<Array<string | number>> = [...metadataRows, [], [], headerRow, ...dataRows];
    const headerRowIndex = metadataRows.length + 2;

    const columnFormats: Array<string | null> = [
        null,
        null,
        buildExcelFormat(yAxisPrefix),
        '0.0"%"',
        ...sizeKpiPrefixes.flatMap((prefix) => [buildExcelFormat(prefix), '0.0"%"']),
    ];

    return { rows: allRows, columnFormats, headerRowIndex, dataRowCount: dataRows.length };
};

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

export const fetchPackArchitectureExportRows = async ({
    scope,
    chartRequest,
    currentRows,
    totalCount,
}: {
    scope: PackArchitectureExportScope;
    chartRequest: PricePackArchitectureRequest | null;
    currentRows: PricePackBubbleRow[];
    totalCount: number;
}): Promise<PricePackBubbleRow[]> => {
    if (scope === 'current_view' || !chartRequest) return currentRows;

    const limit = getEffectiveChartRowCount(chartRequest.user_inputs, totalCount);
    if (limit <= 0) return [];

    const response = await axios.post<{ data?: PricePackBubbleRow[] }>(ppaUrls.getPricePackArchitectureUrl, {
        ...chartRequest,
        offset: 0,
        limit,
    });

    return response.data?.data ?? [];
};
